import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ContentGenerationPopupComponent } from '../content-generation-popup/content-generation-popup.component';

@Component({
  selector: 'app-content-creation',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatExpansionModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatProgressBarModule,
    ContentGenerationPopupComponent
  ],
  templateUrl: './content-creation.component.html',
  styleUrls: ['./content-creation.component.scss']
})
export class ContentCreationComponent implements OnInit, OnChanges {
  @Input() tabData: any = null;
  @Input() config: any = null;
  @Input() evidenceSources: any[] = [];
  @Input() recommendations: any[] = [];

  // Content generation popup functionality
  showContentGenerationPopup: boolean = false;
  selectedPlatform: any = null;
  selectedRecommendation: any = null;

  ngOnInit() {
    // // console.log('🔍 ContentCreationComponent initialized');
    // // console.log('📊 tabData:', this.tabData);
    // // console.log('⚙️  config:', this.config);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tabData']) {
      // // console.log('🔄 tabData changed:', changes['tabData'].currentValue);
      // // console.log('📊 New tabData structure:', {
      //   hasPlatformAnalysis: !!this.tabData?.platform_analysis,
      //   hasUniversities: !!this.tabData?.universities,
      //   platformCategories: this.getPlatformCategories(),
      //   totalPlatforms: this.getAllPlatforms().length
      // });
    }
  }

  getOverallContentScore(): number {
    // For platform-based analysis, calculate score from platform data
    if (this.tabData?.platform_analysis) {
      const platforms = this.getAllPlatforms();
      if (platforms.length === 0) return 0;

      // Calculate score based on platform status
      let totalScore = 0;
      platforms.forEach(platform => {
        switch (platform.current_status?.toLowerCase()) {
          case 'excellent': totalScore += 90; break;
          case 'good foundation': totalScore += 75; break;
          case 'functional but generic': totalScore += 60; break;
          case 'basic presence': totalScore += 45; break;
          case 'limited engagement': totalScore += 30; break;
          case 'underutilized': totalScore += 25; break;
          case 'needs review': totalScore += 20; break;
          default: totalScore += 50;
        }
      });
      return Math.round(totalScore / platforms.length);
    }

    // Fallback to old university-based scoring
    if (!this.tabData?.universities || this.tabData.universities.length === 0) {
      return 0;
    }
    const scores = this.tabData.universities.map((uni: any) => uni.content_status?.content_score || 0);
    return Math.round(scores.reduce((sum: number, score: number) => sum + score, 0) / scores.length);
  }

  getScoreClass(score: number): string {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    return 'score-poor';
  }

  getScoreColor(score: number): string {
    if (score >= 80) return '#28a745';
    if (score >= 60) return '#ffc107';
    return '#dc3545';
  }

  getQualityColor(score: number): string {
    if (score >= 80) return 'primary';
    if (score >= 60) return 'accent';
    return 'warn';
  }

  getCredibilityColor(score: number): string {
    if (score >= 8) return '#28a745';
    if (score >= 6) return '#ffc107';
    return '#dc3545';
  }

  openEvidenceLink(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  // Platform-based analysis methods
  getPlatformCategories(): string[] {
    if (!this.tabData?.platform_analysis) {
      return [];
    }
    return Object.keys(this.tabData.platform_analysis);
  }

  getPlatformsByCategory(category: string): any[] {
    if (!this.tabData?.platform_analysis || !this.tabData.platform_analysis[category]) {
      return [];
    }
    return this.tabData.platform_analysis[category];
  }

  getAllPlatforms(): any[] {
    if (!this.tabData?.platform_analysis) {
      return [];
    }
    const allPlatforms: any[] = [];
    Object.values(this.tabData.platform_analysis).forEach((category: any) => {
      if (Array.isArray(category)) {
        allPlatforms.push(...category);
      }
    });
    return allPlatforms;
  }

  // Content generation popup functionality
  generateContent(platform: any): void {
    // // console.log('=== generateContent called ===');
    // // console.log('Platform data:', platform);
    // // console.log('Config:', this.config);
    // // console.log('Entity label:', this.config?.entityLabel);

    this.selectedPlatform = platform;
    this.showContentGenerationPopup = true;

    // // console.log('selectedPlatform set to:', this.selectedPlatform);
    // // console.log('showContentGenerationPopup set to:', this.showContentGenerationPopup);

    // Test the recommendation data
    const recommendation = this.getRecommendationForPopup();
    // // console.log('Generated recommendation for popup:', recommendation);

    // Force change detection
    setTimeout(() => {
      // // console.log('After timeout - showContentGenerationPopup:', this.showContentGenerationPopup);
      // // console.log('After timeout - recommendation:', this.getRecommendationForPopup());
    }, 100);
  }

  generateContentFromRecommendation(recommendation: any): void {
    this.selectedRecommendation = recommendation;
    this.showContentGenerationPopup = true;
  }

  closeContentGenerationPopup(): void {
    this.showContentGenerationPopup = false;
    this.selectedPlatform = null;
    this.selectedRecommendation = null;
  }

  getRecommendationForPopup(): any {
    if (this.selectedPlatform) {
      return {
        university: this.config?.entityLabel || 'University',
        category: this.selectedPlatform.sub_platform,
        issue: this.selectedPlatform.specific_issue,
        priority: this.selectedPlatform.priority,
        current_score: 0
      };
    } else if (this.selectedRecommendation) {
      return {
        university: this.config?.entityLabel || 'University',
        category: this.selectedRecommendation.content_type,
        issue: this.selectedRecommendation.title,
        priority: 'medium',
        current_score: this.selectedRecommendation.impact_score || 0
      };
    }
    return null;
  }
}

